import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import EmailIcon from '@mui/icons-material/Email';
import { Button, Grid, InputAdornment, MenuItem, Select, Stack, TextField } from '@mui/material';
import { FC, useCallback, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { StudentFormEditInterface, StudentInterface } from '../../interfaces';
import { useTranslation } from '../../services';
import {
	studentValidationSchema,
	studentValidationSchemaWithNotes,
} from './student.validation.schema';
import styles from './Student.module.css';
import { DeactivationReason } from '../../interfaces/deactivationReason.enum';

const deactivationReasonOptions: Array<{ value: DeactivationReason | ''; label: string }> = [
	...Object.values(DeactivationReason).map((val) => ({ value: val, label: val })),
];

const Student: FC<StudentFormEditInterface> = ({
	index,
	value,
	update,
	error,
	withNotes,
}) => {
	const { t } = useTranslation();
	value.notes = value.notes || value.description;
	value.deactivationReason = !!value.deactivationReason ? value.deactivationReason: DeactivationReason.ACTIVE;

	const {
		control: innerControl,
		formState: { errors },
		trigger,
		handleSubmit,
		setError,
	} = useForm({
		defaultValues: value,
		resolver: yupResolver(
			withNotes ? studentValidationSchemaWithNotes : studentValidationSchema
		),
	});

	useEffect(() => {
		const { name, email } = innerControl._defaultValues;
		name !== '' && trigger('name').then();
		email !== '' && trigger('email').then();
	}, []);

	const updateStudent = useCallback(
		(data: StudentInterface) => update(index, data),
		[index, update]
	);

	useEffect(() => {
		if (Array.isArray(error) && error[index]) {
			if (error[index].name) {
				const fixedErrorName = error[index].name;
				fixedErrorName.message = fixedErrorName.message.replace(
					/students\[\d+\]\./,
					''
				);
				setError('name', fixedErrorName);
			}
			if (error[index].email) {
				const fixedErrorEmail = error[index].email;
				fixedErrorEmail.message = fixedErrorEmail.message.replace(
					/students\[\d+\]\./,
					''
				);
				setError('email', fixedErrorEmail);
			}
		}
		if(error){
			trigger('name').then();
			trigger('email').then();
		}
	}, [error]);

	return (
		<>
			<Grid item xs={12} sm={6}>
				<Controller
					name="name"
					control={innerControl}
					render={({ field }) => (
						<TextField
							error={!!errors.name}
							helperText={t(errors.name?.message || '')}
							className={styles.fullWidth}
							variant="outlined"
							label={t('Name')}
							{...field}
							onBlur={!withNotes ? handleSubmit(updateStudent) : undefined}
						/>
					)}
				/>
			</Grid>
			<Grid item xs={12} sm={6}>
				<Controller
					name="email"
					control={innerControl}
					render={({ field }) => (
						<TextField
							required
							error={!!errors.email || !!value.isDublicate}
							helperText={t(errors.email?.message || (value.isDublicate && 'Dublicate email' ) || '')}
							className={styles.fullWidth}
							variant="outlined"
							label={t('Email')}
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">
										<EmailIcon />
									</InputAdornment>
								),
							}}
							{...field}
							onBlur={!withNotes ? handleSubmit(updateStudent) : undefined}
						/>
					)}
				/>
			</Grid>

			{withNotes && (
				<>
					<Grid item xs={12}>
						<Controller
							name="notes"
							control={innerControl}
							render={({ field }) => (
								<TextField
									required
									error={!!errors.notes}
									helperText={t(errors.notes?.message || '')}
									className={styles.fullWidth}
									variant="outlined"
									label={t('Reason')}
									{...field}
								/>
							)}
						/>
					</Grid>
					<Grid item xs={12}>

					<Controller
						name="deactivationReason"
						control={innerControl}
						render={({ field }) => (
						<TextField label={t('Status')} select className={styles.fullWidth} {...field}>
								<MenuItem value=""></MenuItem>
								{deactivationReasonOptions.map((option, i) => (
									<MenuItem key={`reason_${i}`} value={option.value}>
										{t(option.label)}
									</MenuItem>
								))}
						</TextField>
						)}
					/>
					</Grid>
					<Grid item xs={12}>
						<Stack direction="row">
							<Button
								variant="contained"
								onClick={handleSubmit(updateStudent)}
							>
								{t('Save')}
							</Button>
						</Stack>
					</Grid>
				</>
			)}
		</>
	);
};

export default Student;
